<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import i18n from '@/i18n'
import { gameTipsService } from '@/helpers/services/gameTips.service'
import { tenantService } from '@/helpers/services/tenant.service'
import { tenantHelper } from '@/utils'

/**
 * Game tips — the brand's own tip per game.
 *
 * Its own screen next to the knowledge base, and it LOOKS like the knowledge
 * base (owner, 2026-11-02): the same toolbar card with the brand on it, a tab
 * strip across the top (providers here, documents there), and one row per
 * entry with the editor behind a click (games here, questions there). Anyone
 * who has edited the FAQ already knows how this works.
 *
 * What the bot does with these: when a player asks what to play it shares
 * up to three, mixed up, a starred game favoured, a game the player named
 * first. The tip goes out word for word.
 */
const EMPTY = () => ({ provider: '', game: '', tip: '', featured: false })

export default {
  components: { Layout, PageHeader, MultiSelect },
  data() {
    return {
      tenants: [],
      tenantId: null,
      tips: [],
      loading: false,
      busy: false,
      error: '',
      /** '' = all providers; otherwise the provider name; '__none__' = tips without one. */
      tab: '',
      query: '',
      /** id of the open row, or 'new' for the unsaved one at the top. */
      openId: null,
      draft: EMPTY(),
    }
  },
  computed: {
    breadcrumbItems() {
      return [
        { text: i18n.t('module.bot'), href: '/' },
        { text: i18n.t('module.gameTips'), active: true },
      ]
    },
    /** One brand at a time — a tip belongs to exactly one brand, so never "All brands". */
    tenantDdl() {
      return this.tenants.map((t) => ({ text: t.name, value: t.tenant_id }))
    },
    /** The tab strip: every provider in use, counted, plus one for tips with none. */
    providerTabs() {
      const counts = new Map()
      let none = 0
      for (const t of this.tips) {
        if (t.provider) counts.set(t.provider, (counts.get(t.provider) || 0) + 1)
        else none++
      }
      const tabs = [...counts.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, n]) => ({ key: name, name, n }))
      if (none) tabs.push({ key: '__none__', name: this.$t('gameTips.noProvider'), n: none })
      return tabs
    },
    visibleTips() {
      const q = this.query.trim().toLowerCase()
      return this.tips
        .filter((t) => (this.tab === '' ? true : this.tab === '__none__' ? !t.provider : t.provider === this.tab))
        .filter((t) => !q || t.game.toLowerCase().includes(q) || t.tip.toLowerCase().includes(q) || (t.provider || '').toLowerCase().includes(q))
        .slice()
        .sort((a, b) => Number(b.featured) - Number(a.featured) || a.game.localeCompare(b.game))
    },
    canSave() {
      return !this.busy && Boolean(this.draft.game.trim()) && Boolean(this.draft.tip.trim())
    },
  },
  async created() {
    const res = await tenantService.list()
    this.tenants = res.data || []
    // Starts from the top bar's brand when there is one, falls back to the
    // only brand when there is only one — the same rule as the knowledge base.
    this.tenantId = tenantHelper.current()
    if (this.tenantId === null && this.tenants.length === 1) {
      this.tenantId = this.tenants[0].tenant_id
      tenantHelper.set(this.tenantId)
    }
    if (this.tenantId !== null) await this.load()
  },
  methods: {
    readApiError(e) {
      const d = e && e.response && e.response.data
      return (d && (d.error || d.message)) || (e && e.message) || ''
    },
    async chooseTenant(id) {
      if (id === this.tenantId) return
      this.tenantId = id === '' || id === undefined || id === null ? null : Number(id)
      tenantHelper.set(this.tenantId)
      this.tips = []
      this.tab = ''
      this.openId = null
      this.error = ''
      if (this.tenantId !== null) await this.load()
    },
    async load() {
      this.loading = true
      try {
        const res = await gameTipsService.list(this.tenantId)
        this.tips = (res && res.data) || []
        this.error = ''
      } catch (e) {
        this.error = this.readApiError(e) || 'Could not load the game tips.'
      } finally {
        this.loading = false
      }
    },
    pickTab(key) {
      this.tab = key
      if (this.openId !== 'new') this.openId = null
    },
    /** A new row at the top, open, with the current tab's provider filled in. */
    startNew() {
      if (!this.tenantId) return
      this.draft = EMPTY()
      if (this.tab && this.tab !== '__none__') this.draft.provider = this.tab
      this.openId = 'new'
      this.$nextTick(() => this.$refs.gameInput && this.$refs.gameInput.focus())
    },
    toggleRow(t) {
      if (this.openId === t.id) {
        this.openId = null
        return
      }
      this.openId = t.id
      this.draft = { provider: t.provider || '', game: t.game, tip: t.tip, featured: Boolean(t.featured) }
    },
    cancel() {
      this.openId = null
    },
    async save(t) {
      if (!this.canSave) return
      this.busy = true
      const payload = {
        provider: this.draft.provider.trim() || null,
        game: this.draft.game.trim(),
        tip: this.draft.tip.trim(),
        featured: Boolean(this.draft.featured),
      }
      try {
        if (t) {
          const res = await gameTipsService.update(this.tenantId, t.id, payload)
          if (res && res.data) Object.assign(t, res.data)
        } else {
          const res = await gameTipsService.create(this.tenantId, payload)
          if (res && res.data) this.tips.push(res.data)
        }
        this.openId = null
        this.error = ''
      } catch (e) {
        this.error = this.readApiError(e) || 'Could not save the tip.'
      } finally {
        this.busy = false
      }
    },
    /** The star on the row: one click, no need to open the editor. */
    async toggleFeatured(t) {
      try {
        const res = await gameTipsService.update(this.tenantId, t.id, { featured: !t.featured })
        if (res && res.data) Object.assign(t, res.data)
        if (this.openId === t.id) this.draft.featured = Boolean(t.featured)
      } catch (e) {
        this.error = this.readApiError(e) || 'Could not update the tip.'
      }
    },
    async remove(t) {
      const ok = await this.$bvModal.msgBoxConfirm(this.$t('gameTips.deleteConfirm', [t.game]), {
        okVariant: 'danger',
        okTitle: this.$t('gameTips.delete'),
        cancelTitle: this.$t('gameTips.cancel'),
        centered: true,
      })
      if (!ok) return
      try {
        await gameTipsService.remove(this.tenantId, t.id)
        this.tips = this.tips.filter((x) => x.id !== t.id)
        if (this.openId === t.id) this.openId = null
        // The tab it sat on may be gone now.
        if (this.tab && !this.providerTabs.some((p) => p.key === this.tab)) this.tab = ''
        this.error = ''
      } catch (e) {
        this.error = this.readApiError(e) || 'Could not remove the tip.'
      }
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('module.gameTips')" :items="breadcrumbItems" />

    <!-- The toolbar card, the same one the knowledge base has: the brand lives
         HERE, and the one action that starts something sits beside it. -->
    <div class="card mb-3">
      <div class="card-body py-2 d-flex flex-wrap align-items-center">
        <div class="brand-pick mr-3 my-1">
          <MultiSelect
            :value="tenantId"
            :options="tenantDdl"
            :multiple="false"
            :allow-empty="false"
            :placeholder="$t('label.brand')"
            @input="chooseTenant"
          />
        </div>

        <div class="vr d-none d-md-block mr-3"></div>

        <button class="btn btn-sm btn-outline-primary mr-2 my-1" :disabled="!tenantId" @click="startNew">
          <i class="mdi mdi-plus mr-1"></i>{{ $t('gameTips.newTip') }}
        </button>

        <div class="small text-muted ml-auto my-1 pl-3 text-right">{{ $t('gameTips.hint') }}</div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <!-- One card: providers as tabs across the top, one game per row below.
         Documents and questions on the knowledge base; providers and games
         here. -->
    <div class="card">
      <div class="doc-tabs">
        <button type="button" class="doc-tab" :class="{ active: tab === '' }" :disabled="!tenantId" @click="pickTab('')">
          <span class="doc-tab-name">{{ $t('gameTips.allProviders') }}</span>
          <span class="doc-tab-count">{{ tips.length }}</span>
        </button>
        <button v-for="p in providerTabs" :key="p.key" type="button" class="doc-tab"
                :class="{ active: tab === p.key }" @click="pickTab(p.key)">
          <span class="doc-tab-name">{{ p.name }}</span>
          <span class="doc-tab-count">{{ p.n }}</span>
        </button>
        <span v-if="loading" class="small text-muted align-self-center ml-2">{{ $t('label.loading') }}</span>
        <span v-else-if="!tenantId" class="small text-muted align-self-center ml-2">{{ $t('gameTips.pickBrand') }}</span>
      </div>

      <div class="p-3">
        <div class="d-flex align-items-center mb-2">
          <strong class="small mr-auto">{{ $t('gameTips.count', [visibleTips.length]) }}</strong>
          <input v-model.trim="query" type="search" class="form-control form-control-sm q-search"
                 :placeholder="$t('gameTips.search')" />
        </div>

        <div class="q-list">
          <!-- The unsaved one, at the top, already open. -->
          <div v-if="openId === 'new'" class="q-item open">
            <div class="q-head">
              <i class="mdi q-caret mdi-chevron-down"></i>
              <span class="q-title">{{ draft.game || $t('gameTips.newTip') }}</span>
            </div>
            <div class="q-body">
              <label class="small text-muted mb-1 d-block">{{ $t('gameTips.game') }}</label>
              <input ref="gameInput" v-model="draft.game" type="text" class="form-control form-control-sm mb-2" maxlength="120"
                     :placeholder="$t('gameTips.gamePh')" @keyup.esc="cancel" />
              <label class="small text-muted mb-1 d-block">{{ $t('gameTips.provider') }}</label>
              <input v-model="draft.provider" type="text" class="form-control form-control-sm mb-2" maxlength="64"
                     :placeholder="$t('gameTips.providerPh')" />
              <label class="small text-muted mb-1 d-block">{{ $t('gameTips.tip') }}</label>
              <textarea v-model="draft.tip" class="form-control form-control-sm mb-2" rows="3" maxlength="2000"
                        :placeholder="$t('gameTips.tipPh')"></textarea>
              <div class="handoff-box mb-3">
                <b-form-checkbox v-model="draft.featured" switch><b>{{ $t('gameTips.featuredLabel') }}</b></b-form-checkbox>
                <small class="form-text text-muted">{{ $t('gameTips.featuredHint') }}</small>
              </div>
              <button class="btn btn-sm btn-primary mr-2" :disabled="!canSave" @click="save(null)">{{ $t('gameTips.save') }}</button>
              <button class="btn btn-sm btn-light" @click="cancel">{{ $t('gameTips.cancel') }}</button>
            </div>
          </div>

          <div v-if="!visibleTips.length && openId !== 'new'" class="small text-muted py-4 text-center">
            {{ !tenantId ? $t('gameTips.pickBrand') : query ? $t('gameTips.noMatch') : $t('gameTips.empty') }}
          </div>

          <div v-for="t in visibleTips" :key="t.id" class="q-item" :class="{ open: openId === t.id }">
            <div class="q-head" @click="toggleRow(t)">
              <i class="mdi q-caret" :class="openId === t.id ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
              <button type="button" class="q-star" :class="{ on: t.featured }"
                      :title="t.featured ? $t('gameTips.unfeature') : $t('gameTips.feature')" @click.stop="toggleFeatured(t)">
                <i class="mdi" :class="t.featured ? 'mdi-star' : 'mdi-star-outline'"></i>
              </button>
              <span class="q-title">
                {{ t.game }}
                <span v-if="tab === '' && t.provider" class="q-provider">{{ t.provider }}</span>
                <span class="q-preview">— {{ t.tip }}</span>
              </span>
              <button type="button" class="q-del" :title="$t('gameTips.delete')" @click.stop="remove(t)">&times;</button>
            </div>

            <div v-if="openId === t.id" class="q-body">
              <label class="small text-muted mb-1 d-block">{{ $t('gameTips.game') }}</label>
              <input v-model="draft.game" type="text" class="form-control form-control-sm mb-2" maxlength="120" @keyup.esc="cancel" />
              <label class="small text-muted mb-1 d-block">{{ $t('gameTips.provider') }}</label>
              <input v-model="draft.provider" type="text" class="form-control form-control-sm mb-2" maxlength="64"
                     :placeholder="$t('gameTips.providerPh')" />
              <label class="small text-muted mb-1 d-block">{{ $t('gameTips.tip') }}</label>
              <textarea v-model="draft.tip" class="form-control form-control-sm mb-2" rows="3" maxlength="2000"></textarea>
              <p class="small text-muted mb-3">{{ $t('gameTips.howItWorks') }}</p>
              <div class="handoff-box mb-3">
                <b-form-checkbox v-model="draft.featured" switch><b>{{ $t('gameTips.featuredLabel') }}</b></b-form-checkbox>
                <small class="form-text text-muted">{{ $t('gameTips.featuredHint') }}</small>
              </div>
              <button class="btn btn-sm btn-primary mr-2" :disabled="!canSave" @click="save(t)">{{ $t('gameTips.save') }}</button>
              <button class="btn btn-sm btn-light" @click="cancel">{{ $t('gameTips.cancel') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Lifted from the knowledge base page so the two screens read as one family. */
.brand-pick { min-width: 200px; }
.vr { width: 1px; align-self: stretch; background: #eef0f4; margin: 4px 0; }

.doc-tabs {
  display: flex;
  align-items: stretch;
  gap: 2px;
  padding: 8px 10px 0;
  border-bottom: 1px solid #e3e6ed;
  background: #f7f8fb;
  overflow-x: auto;
  scrollbar-width: thin;
}
.doc-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  max-width: 280px;
  border: 1px solid transparent;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  background: transparent;
  color: #5c6478;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 12px;
  margin-bottom: -1px;
  cursor: pointer;
}
.doc-tab:hover { background: #eef1f7; color: #2b2f3a; }
.doc-tab.active { background: #fff; color: #2b2f3a; border-color: #e3e6ed; border-bottom: 1px solid #fff; }
.doc-tab:disabled { opacity: 0.5; cursor: default; }
.doc-tab-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.doc-tab-count { flex: none; font-size: 11px; font-weight: 700; color: #8a93aa; background: #eef1f7; border-radius: 9px; padding: 1px 6px; }
.doc-tab.active .doc-tab-count { background: #f1eefc; color: #5a2ea6; }

.q-search { width: 220px; }
.q-list { border: 1px solid #e3e6ed; border-radius: 6px; min-height: 200px; }
.q-item + .q-item { border-top: 1px solid #eef0f4; }
.q-head { display: flex; align-items: center; gap: 6px; padding: 9px 12px; cursor: pointer; background: #fbfcfe; }
.q-head:hover { background: #f3f5fb; }
.q-item.open > .q-head { background: #f3f0fd; }
.q-caret { color: #8a93aa; font-size: 16px; line-height: 1; }
.q-title { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 600; color: #2b2f3a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.q-provider { font-size: 11px; font-weight: 700; color: #5a2ea6; background: #f1eefc; border-radius: 9px; padding: 1px 7px; margin-left: 6px; vertical-align: middle; }
.q-preview { font-weight: 400; color: #8a93aa; margin-left: 6px; }
.q-star { background: none; border: 0; padding: 0 2px; font-size: 17px; line-height: 1; color: #c9ced9; }
.q-star.on { color: #f1b44c; }
.q-star:hover { color: #f1b44c; }
.q-del { background: none; border: 0; color: #c9ced9; font-size: 18px; line-height: 1; padding: 0 4px; text-decoration: none; }
.q-head:hover .q-del { color: #a8322c; }
.q-body { padding: 12px 14px 14px 30px; background: #fff; }
.handoff-box { border: 1px solid #eef0f4; border-radius: 6px; padding: 10px 12px; background: #fafbfd; }
</style>
